
import Order from "@/models/Order";
import Product from "@/models/Product";
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import OrderReplacementItemNew from "./OrderReplacementItemNew.vue";
import CloseIcon from "@/components/UI/Icons/CloseIcon.vue";
import UIButton from "@/components/UI/Button/UIButton.vue";
import InitLoader from "@/components/UI/Loader/InitLoader.vue";
import { _axios } from "@/apiManager/_requestHelper";
import { RouteInformation } from "@/routerManagerNew/routes";

export type IItemsToManipulate = {
  originalItem: Product;
  replacementItem?: Product;
  alternativeItems?: IItemsToManipulate[];
  isDiscarded: Boolean;
  isEmployeeDiscarded: Boolean;
  quantity: number;
  tempQuantity: number;
  updatedAt?: number;
}

export default Vue.extend({

  components: {
    "item-component": OrderReplacementItemNew,
    "close-icon": CloseIcon,
    "ui-button": UIButton,
    "init-loader": InitLoader
  },

  data: () => ({
    itemsToManipulate: [] as IItemsToManipulate[],
    currentSelectedObject: {} as IItemsToManipulate,
    currentSelectedReplacement: {} as Product,
    dialog: false as boolean,
    alternativesLoading: false as boolean,
    submitted: false as boolean,
    loading: false as boolean,
    countdown: 0 as number,
    countdownInterval: 0 as any,
    showExtendTimePopup: false as boolean,
    orderReplacementProcessStarted: false as boolean
  }),

  computed: {
    ...mapState("order", ["singleOrder"]),
    ...mapState("loading", ["singleOrderLoading"]),
    ...mapState("partner", ["partnerSettings"]),
    ...mapState("ui", ["isMobileViewDisplayed"]),

    /**
     * Order object from server
    */
    orderDetails(): Order {
      return (this as any).singleOrder as Order;
    },

    submitButtonLabel(): string {


      if(this.allItemsAreHandled) {
        return "SUBMIT YOUR CHANGES"
      }

      else if(this.orderReplacementProcessStarted) {
        var itemsProcessed = 0;
        var totalItems = this.itemsToManipulate.length;

        itemsProcessed = this.itemsToManipulate.filter(x => x.isEmployeeDiscarded)?.length ?? 0;

        this.itemsToManipulate.filter(x=>  !x.isEmployeeDiscarded).forEach(item => {
          if(item.updatedAt != null)
            itemsProcessed++;
        });

        return `${itemsProcessed}/${totalItems} Replaced`
      }

      else {
        var removedCount = 0;
        var toBeReplacedCount = 0;

        for (const item of this.itemsToManipulate) {
          if (!item.originalItem.hasAlternatives || item.originalItem.isEmployeeForceDiscarded ) {
            removedCount++;
          } else {
            toBeReplacedCount++
          }
        }

        if(removedCount == 0)
          return `${toBeReplacedCount} To Be Replaced`;

        return `${removedCount} Removed  |  ${toBeReplacedCount} To Be Replaced`;
      }
    },

    allItemsAreHandled(): boolean {
      return this.itemsToManipulate.every(x => x.updatedAt != null || x.isEmployeeDiscarded);
    },

    countdownLessThenFiveMins(): boolean {
      return this.countdown < (5 * 60)
    }
  },


  methods: {
    ...mapActions("order", ["singleOrderById", "placeAlternativeOrderRequest"]),

    /**
     * Get order details from server and manipulate data
     */
    async getOrderDetails() {
      this.loading = true;
      // get order id from route
      var orderId = this.$route.params["orderId"];
      
      if(typeof orderId == "undefined" || orderId == null)
        return;

      // get order from server
      await this.singleOrderById({
        id: orderId,
        includeAlternatives: true
      });

      // check if order has returned
      if(this.orderDetails != null && this.orderDetails.items != null && this.orderDetails.items.length > 0) {
        

       this.orderDetails.items.forEach((i) => {
          if(i.alternativeItems != null) {
            i.alternativeItems = i.alternativeItems.sort((a, b) => {
              return Number(a.id !== i.id) - Number(b.id !== i.id);
            });
          }
        });

        // fill items to use
        this.orderDetails.items
          .filter((i) => !i.isAlreadyCollected && i.isPurchasedItemOutOfStock)
          .map((itemFromServer) => this.itemsToManipulate.push({
            originalItem: itemFromServer,
            isDiscarded: false,
            isEmployeeDiscarded: !itemFromServer.hasAlternatives || itemFromServer.isEmployeeForceDiscarded, 
            quantity: itemFromServer.actualQtyOnHand ? itemFromServer.actualQtyOnHand : itemFromServer.purchasedQuantity,
            tempQuantity: itemFromServer.actualQtyOnHand ? itemFromServer.actualQtyOnHand : itemFromServer.purchasedQuantity,
            alternativeItems: itemFromServer.alternativeItems?.map(x => {
              return {
                originalItem: x
              } as IItemsToManipulate
            })
          }));

        if(this.itemsToManipulate.length > 0) {
          this.sortItemsFromServerByStatus();
        }
      }

      this.startCountdown();

      this.loading = false;
    },

    //#region Sorting functions
    sortItemsFromServerByStatus() {
      this.itemsToManipulate = this.itemsToManipulate
        .sort((a, b) => {
          const aItem = a.originalItem;
          const bItem = b.originalItem;

          const aPriority = (aItem.hasAlternatives && !aItem.isEmployeeForceDiscarded) ? 0 : 1;
          const bPriority = (bItem.hasAlternatives && !bItem.isEmployeeForceDiscarded) ? 0 : 1;

          return aPriority - bPriority;
        });
    },

    
    sortByTime() {
      this.itemsToManipulate.sort((a, b) => {
        const aTime = a.updatedAt ?? 0;
        const bTime = b.updatedAt ?? 0;
        return aTime - bTime; // ascending: oldest to newest
      });
    },
    //#endregion
  
    openItemDetailsDialog(itemObject: IItemsToManipulate) {
      this.currentSelectedObject = itemObject;

      var found = this.itemsToManipulate.find(x => x.originalItem.id == itemObject.originalItem.id);
      if(found != null) {
        if(found.replacementItem != null) {
          this.currentSelectedReplacement = found.replacementItem
        }
      }

      this.dialog = true;
    },

    decreaseQuantity(item: IItemsToManipulate) {
      const index = this.itemsToManipulate.findIndex(x => x.originalItem.id === item.originalItem.id);
      if (index !== -1) {
        var quantity = this.itemsToManipulate[index].tempQuantity;
        if(quantity > 1)
          quantity--;

        this.itemsToManipulate.splice(index, 1, {
          ...this.itemsToManipulate[index],
          tempQuantity: quantity
        });

        this.currentSelectedObject.quantity = quantity;
        this.currentSelectedObject.tempQuantity = quantity;
      
      }
    },

    increaseQuantity(item: IItemsToManipulate) {
      const index = this.itemsToManipulate.findIndex(x => x.originalItem.id === item.originalItem.id);
      if (index !== -1) {
        var quantity = this.itemsToManipulate[index].tempQuantity;
        var item = this.itemsToManipulate[index];

        if(item.originalItem.id == this.currentSelectedReplacement.id) {
          if(quantity < item.originalItem.actualQtyOnHand) {
            quantity++
          }
        }

        else if(quantity < this.currentSelectedReplacement.maxOrder){
          quantity++;
        }
          
        this.itemsToManipulate.splice(index, 1, {
          ...this.itemsToManipulate[index],
          tempQuantity: quantity
        });

        this.currentSelectedObject.quantity = quantity;
        this.currentSelectedObject.tempQuantity = quantity;

      }
    },

    discardItem() {
      const index = this.itemsToManipulate.findIndex(x => x.originalItem.id === this.currentSelectedObject.originalItem.id);
      if (index !== -1) {
        this.itemsToManipulate.splice(index, 1, {
          ...this.itemsToManipulate[index],
          isDiscarded: true,
          replacementItem: null as any,
          updatedAt: new Date().getMilliseconds()
        }); 

        this.sortByTime();
        this.dialog = false;
        this.orderReplacementProcessStarted = true;
      }
    },

    selectAlternative() {
      if(this.currentSelectedReplacement.id > 0) {
        const index = this.itemsToManipulate.findIndex(x => x.originalItem.id === this.currentSelectedObject.originalItem.id);
        if (index !== -1) {
          this.itemsToManipulate.splice(index, 1, {
            ...this.itemsToManipulate[index],
            isDiscarded: false,
            replacementItem: this.currentSelectedReplacement,
            updatedAt: new Date().getMilliseconds(),
            quantity: this.itemsToManipulate[index].tempQuantity
          });

          this.sortByTime();
          this.dialog = false;
        }

        this.currentSelectedObject = {} as IItemsToManipulate;
        this.currentSelectedReplacement = {} as Product;

        this.orderReplacementProcessStarted = true;
      }
    },

    // submit alternatives request
    async submitAlternatives() {
      if(this.allItemsAreHandled == true) {
        this.alternativesLoading = true;
        var payload = {
          id: this.$route.params["orderId"],
          items: this.itemsToManipulate.filter(x => x.originalItem.hasAlternatives && !x.originalItem.isEmployeeForceDiscarded).map((r) => {
            return {
              oosOrderItemId: r.originalItem.orderItemId,
              replacementInvId: r.replacementItem != null ? r.replacementItem.id : null,
              replacementQuantity: r.quantity,
            };
          }),
        };
        await this.placeAlternativeOrderRequest(payload)
          .then(() => {
            this.alternativesLoading = false;
            this.submitted= true;
          })
          .catch(() => {
            this.alternativesLoading = false;
          });
      }
    },

    goToOrdersPage() {
      this.$router.push({name: RouteInformation.OrdersPage})
    },

    // countdown timer
    startCountdown() {
      clearInterval(this.countdownInterval);

      const futureTime: Date = new Date(this.orderDetails.maxWaitingTimeForAlternatives);
      const now: Date = new Date();

      // Ensure both are Date objects and subtracting gives a number
      const diffMs: number = futureTime.getTime() - now.getTime();

      this.countdown = Math.floor(Math.max(diffMs, 0) / 60000) * 60;

      this.countdownInterval = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;

          // At 5 minutes left
          if (this.countdown === 300) {
            this.showExtendTimePopup = true;
          }
        }

        if (this.countdown === 0) {
          clearInterval(this.countdownInterval!);
          this.countdownInterval = null;
        }
      }, 1000);
    },
  },

  async mounted() {
   

    var token = this.$route.query["token"];
    if(typeof token != "undefined" && token != null)
        _axios.defaults.headers.common["Authorization"] = "Bearer " + this.$route.query["token"];

    await this.getOrderDetails();
  },

  watch : {
    "currentSelectedReplacement.id"(val: number) {
      if(val != null && val >  0) {
        const index = this.itemsToManipulate.findIndex(x => x.originalItem.id === this.currentSelectedObject.originalItem.id);
        if (index !== -1) {
          var item = this.itemsToManipulate[index];

          var qtyToUse = item.originalItem.purchasedQuantity > this.currentSelectedReplacement.maxOrder 
                          ? this.currentSelectedReplacement.maxOrder 
                          : item.originalItem.purchasedQuantity;

          if(this.currentSelectedReplacement.id == item.originalItem.id && item.originalItem.actualQtyOnHand && item.originalItem.actualQtyOnHand > 0) {
            qtyToUse = item.originalItem.actualQtyOnHand;
          }

          if(item.updatedAt != null) {
            qtyToUse = item.quantity;
          }

          this.itemsToManipulate.splice(index, 1, {
            ...item,
            tempQuantity: qtyToUse
          });

          this.currentSelectedObject.tempQuantity = qtyToUse
          this.currentSelectedObject.quantity = qtyToUse

        }
      }
    },
  }
})
